<template>
  <div class="record-label-selector">
    <v-dialog
      v-model="dialogVisible"
      max-width="600"
      content-class="account_selection_wizard_modal"
      persistent
      :retain-focus="false"
      :key="'main-dialog-' + dialogKey"
    >
      <div :class="{'dimmed-dialog': dialogPaused}">
        <!-- Overlay for dimming when subscription dialog is shown -->
        <div class="dialog-overlay" v-if="dialogPaused"></div>
        <!-- Modal Header styled like wizard-header -->
      <div class="wizard-header">
        <h2 class="heading_wizard">Select Record Label</h2>
        <button class="close_modal" @click="closeDialog"></button>
      </div>

      <!-- Wrapper Content with wizard-content to ensure vertical layout -->
      <div class="wrapper_content">
        <div class="wizard-content">
          <!-- Create New Account Section - Exactly like in AccountSelectionWizard -->
          <div class="box_create_button" @click="openLabelCreationDialog" :class="{'active': showLabelCreationDialog}">
            <button type="button" :class="{'active': showLabelCreationDialog}"></button>
            <div>
              Create new record label
              <span>Click here to create a new label account</span>
            </div>
          </div>
          
          <!-- Record Labels List -->
          <div class="list_accounts primary-artist-list">
            <!-- Loading State -->
            <div v-if="isLoading" class="list-state-container">
              <v-progress-circular :size="40" :width="3" color="#20C4F5" indeterminate></v-progress-circular>
              <span class="loading-message">Loading record labels...</span>
            </div>

            <!-- Empty State -->
            <div v-else-if="!recordLabels.length" class="list-state-container">
              <div class="empty-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="#7481A6"/>
                </svg>
              </div>
              <span>No record labels found</span>
            </div>

            <!-- No Search Results -->
            <div v-else-if="searchQuery && !filteredLabels.length" class="list-state-container">
              <div class="empty-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="#7481A6"/>
                </svg>
              </div>
              <span>No results found for "{{searchQuery}}"</span>
            </div>

            <!-- Labels List -->
            <template v-else>
              <div 
                v-for="label in filteredLabels" 
                :key="label.id" 
                class="item_account"
                :class="{'selected': selectedLabel && selectedLabel.id === label.id}"
                @click="selectLabel(label)"
              >
                <div class="data">
                  <div class="img">
                    <img 
                      v-if="label.logo && label.logo.thumbnail && label.logo.thumbnail['50x50']" 
                      :src="getLogoUrl(label)"
                      :srcset="`${getLogoUrl(label)} 1x, ${label.logo.thumbnail['50x50@2x']}?cache=${new Date().getTime()} 2x`"
                      :alt="label.title"
                      @error="handleAvatarError"
                    />
                    <div class="default_user_avatar" v-else>
                      <img :src="require('@/assets/image/svg/square_avatar.svg')" alt="Default avatar">
                    </div>
                  </div>
                  <div class="description">
                    <div class="name">{{ label.title }}</div>
                    <div class="type" v-if="!hasRestrictedStatus(label)">{{ label['@type'] }}</div>
                    <SubscriptionStatus v-if="hasRestrictedStatus(label)" :subscription="label.subscription"/>
                  </div>
                </div>
                <div class="action">
                  <div class="check_round" :class="{checked: selectedLabel && selectedLabel.id === label.id}"></div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Modal Actions styled like wrapper_button -->
      <div class="wrapper_button center">
        <button type="button" class="tetriatary_btn" v-ripple @click="closeDialog">Back</button>
        <button 
          type="button" 
          class="tetriatary_btn" 
          v-ripple
          :class="{disabled: !selectedLabel}"
          :disabled="!selectedLabel"
          @click="confirmSelection"
        >{{ isVariousArtistsFlow ? 'Finish' : 'Next' }}</button>
      </div>
      </div>
    </v-dialog>

    <!-- Creation Dialog for new record label -->
    <record-label-creation-dialog
      v-if="showLabelCreationDialog"
      v-model="showLabelCreationDialog"
      account-type="Label"
      @cancel="onLabelCreationCancel"
      @created="onLabelCreated"
    />

    <!-- Creator Account Renewal Dialog -->
    <creator-account-renewal-dialog
      v-if="showRenewalDialog"
      :value="showRenewalDialog"
      :account="labelToRenew"
      :key="'renewal-dialog-' + dialogKey"
      @input="val => showRenewalDialog = val"
      @cancel="onRenewalCancel"
      @success="onRenewalSuccess"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import CreatorAccountRenewalDialog from './CreatorAccountRenewalDialog.vue';
import SubscriptionStatus from '@/components/subscriptionStatus.vue';

export default {
  name: 'RecordLabelSelector',
  components: {
    'creator-account-renewal-dialog': CreatorAccountRenewalDialog,
    // Use the AccountCreationDialog component for label creation
    'record-label-creation-dialog': () => import('./AccountCreationDialog.vue'),
    SubscriptionStatus
  },
  props: {
    // Using a v-model pattern for the dialog visibility
    value: {
      type: Boolean,
      default: false
    },
    // Allow a pre-selected label to be passed in
    initialSelectedLabel: {
      type: Object,
      default: null
    },
    // Flag to indicate if this is being used in Various Artists flow
    isVariousArtistsFlow: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialogKey: 0, // Used to force dialog recreation
      searchQuery: '',
      isLoading: false,
      filteredLabels: [],
      selectedLabel: null,
      showLabelCreationDialog: false,
      showRenewalDialog: false,
      labelToRenew: null,
      searchJustCleared: false,
      dialogPaused: false
    };
  },
  computed: {
    ...mapGetters({
      recordLabels: 'YOUR_RECORD_LABELS',
      loadingRecordLabels: 'LOADING_YOUR_RECORD_LABELS',
      currentLabel: 'SELECTED_LABEL',
      userID: 'userID'
    }),
    // Handle v-model for dialog visibility
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    }
  },
  watch: {
    // Watch for external changes to the dialog visibility
    value(newVal) {
      if (newVal) {
        // When dialog opens, check both props and store for selected label
        if (this.initialSelectedLabel) {
          this.selectedLabel = this.initialSelectedLabel;
        } else if (this.currentLabel) {
          console.log('Dialog opened, found selected label in store:', this.currentLabel);
          this.selectedLabel = this.currentLabel;
        }
        
        // Initialize the rest of the component
        this.initialize();
      }
    },
    // Watch for changes to the record labels from the store
    recordLabels: {
      handler(newLabels) {
        this.filteredLabels = [...newLabels];
        this.filterLabels();
      },
      immediate: true
    },
    // Watch for changes to the selected label in the store
    currentLabel: {
      handler(newLabel) {
        if (newLabel) {
          this.selectedLabel = newLabel;
        }
      },
      immediate: true
    }
  },
  created() {
    // Load record labels when component is created, filtered by current user
    this.initialize();
  },
  methods: {
    ...mapActions([
      'GET_YOUR_RECORD_LABELS'
    ]),
    ...mapMutations([
      'SET_SELECTED_LABEL'
    ]),
    initialize() {
      this.isLoading = true;
      
      // Use a simpler approach without additional parameters that might cause API errors
      this.GET_YOUR_RECORD_LABELS()
        .then(() => {
          // If we have record labels, filter them client-side to show only the current user's
          if (this.recordLabels && Array.isArray(this.recordLabels)) {
            // Filter labels by current user ID if available
            this.filteredLabels = this.userID ? 
              this.recordLabels.filter(label => label.owner && label.owner.id === this.userID) : 
              [...this.recordLabels];
          } else {
            this.filteredLabels = [];
          }

          // Initialize selectedLabel from props or store
          // First check if initialSelectedLabel prop was provided
          if (this.initialSelectedLabel) {
            this.selectedLabel = this.initialSelectedLabel;
          } 
          // If not, check if there's a selected label in the store
          else if (this.currentLabel) {
            console.log('Found selected label in Vuex store, using it as default:', this.currentLabel);
            this.selectedLabel = this.currentLabel;
          }
          
          this.isLoading = false;
        })
        .catch(error => {
          console.error('Error loading record labels:', error);
          this.filteredLabels = [];
          this.isLoading = false;
        });
    },
    filterLabels() {
      if (!this.searchQuery) {
        this.filteredLabels = [...this.recordLabels];
        return;
      }
      
      const query = this.searchQuery.toLowerCase();
      this.filteredLabels = this.recordLabels.filter(label => {
        return label.title.toLowerCase().includes(query);
      });
    },
    clearSearch() {
      this.searchQuery = '';
      this.filteredLabels = [...this.recordLabels];
      this.searchJustCleared = true;
      setTimeout(() => this.resetSearchJustCleared(), 100);
    },
    
    resetSearchJustCleared() {
      this.searchJustCleared = false;
    },
    
    focusSearchInput(event) {
      // Only focus if we're clicking directly on the search box, not on the clear button
      if (!this.searchJustCleared && this.$refs.searchInput) {
        this.$refs.searchInput.focus();
      }
    },
    
    handleSearchFocus() {
      // Handle any special behavior when the search input gets focus
    },
    selectLabel(label) {
      // Prevent processing if we're already handling a label with issues
      if (this.dialogPaused) {
        console.log('Dialog already paused, ignoring selection');
        return;
      }

      // Set the selected label first, so we know which label to renew if needed
      this.selectedLabel = label;
      
      // Check if the label has subscription issues
      if (this.hasRestrictedStatus(label)) {
        // Use nextTick to avoid focus-related recursion
        this.$nextTick(() => {
          // Show the subscription renewal dialog
          // Make sure we explicitly mark this as a Label for the renewal process
          this.labelToRenew = {
            ...label,
            '@type': 'Label'  // Explicitly set type to 'Label'
          };
          console.log('Prepared label for renewal:', this.labelToRenew);
          this.dialogPaused = true; // Set this first to prevent recursion
          this.showRenewalDialog = true;
        });
        return; // Don't proceed with selection until subscription is renewed
      }
    },
    confirmSelection() {
      if (this.selectedLabel) {
        // Check again if the label has subscription issues before proceeding
        // This handles cases where subscription status might have changed during selection
        if (this.hasRestrictedStatus(this.selectedLabel) && !this.dialogPaused) {
          // Use nextTick to avoid focus-related recursion
          this.$nextTick(() => {
            // Show the subscription renewal dialog
            this.labelToRenew = {
              ...this.selectedLabel,
              '@type': 'Label'  // Explicitly set type to 'Label'
            };
            console.log('Subscription issue detected during confirmation, opening renewal dialog');
            this.dialogPaused = true; // Set this first to prevent recursion
            this.showRenewalDialog = true;
          });
          return; // Don't proceed with selection until subscription is renewed
        }
        
        // Update the store with the selected label
        this.SET_SELECTED_LABEL(this.selectedLabel);
        
        // Emit events for the parent component
        this.$emit('label-selected', this.selectedLabel);
        
        // Close the dialog
        this.closeDialog();
      }
    },
    closeDialog() {
      this.$emit('input', false);
      this.$emit('dialog-closed');
    },
    getLogoUrl(label) {
      if (label.logo && label.logo.thumbnail && label.logo.thumbnail['50x50']) {
        const cacheTime = new Date().getTime();
        return `${label.logo.thumbnail['50x50']}?cache=${cacheTime}`;
      }
      return '';
    },
    handleAvatarError(event) {
      // Hide the broken image and show the default avatar
      event.target.style.display = 'none';
      event.target.nextElementSibling.style.display = 'flex';
    },
    hasRestrictedStatus(label) {
      if (!label) return false;
      
      const rule1 = !label.hasActiveSubscription && !label.subscription;
      const rule2 = !label.hasActiveSubscription && label.subscription && !label.subscription.canActivate;
      
      return rule1 || rule2;
    },
    getSubscriptionStatusText(label) {
      // No subscription or can't activate
      if (!label.hasActiveSubscription) {
        if (!label.subscription) {
          return 'Expired';
        } else if (!label.subscription.canActivate) {
          if (label.subscription.status === 'past_due') {
            return 'Billing issues';
          } else {
            return 'Expired';
          }
        }
      }
      
      return '';
    },
    hasExpiresStatus(label) {
      if (!label) return false;
      return label.subscription && label.subscription.status && label.subscription.status === 'past_due';
    },
    openLabelCreationDialog() {
      this.showLabelCreationDialog = true;
      this.dialogPaused = true;
    },
    onLabelCreationCancel() {
      this.showLabelCreationDialog = false;
      this.dialogPaused = false;
    },
    onLabelCreated(newLabelName) {
      this.showLabelCreationDialog = false;
      
      // Refresh the record labels list
      this.GET_YOUR_RECORD_LABELS().then(() => {
        // Find the newly created label in the updated list
        const createdLabel = this.recordLabels.find(label => label.title === newLabelName);
        
        if (createdLabel) {
          // Select the newly created label
          this.selectLabel(createdLabel);
        }
      });
    },
    openRenewalDialog(label) {
      this.labelToRenew = label;
      this.showRenewalDialog = true;
      this.dialogPaused = true;
    },
    onRenewalCancel() {
      // Prevent further focus events by blurring any active element
      if (document.activeElement) {
        document.activeElement.blur();
      }
      
      // Use a local copy of the selected label to avoid reactivity issues
      const selectedLabelWasRestricted = this.selectedLabel && this.hasRestrictedStatus(this.selectedLabel);
      
      // First close the subscription dialog
      this.showRenewalDialog = false;
      this.labelToRenew = null;
      
      // Force dialog recreation on next opening to break focus chains
      this.dialogKey++;
      
      // Clear selected label immediately if restricted
      if (selectedLabelWasRestricted) {
        this.selectedLabel = null;
      }
      
      // Resume main dialog after a short delay
      setTimeout(() => {
        this.dialogPaused = false;
      }, 50);
    },
    onRenewalSuccess(renewedLabel) {
      console.log('Subscription renewal successful');
      
      // Close the renewal dialog first, before updating data
      this.showRenewalDialog = false;
      this.labelToRenew = null;
      
      // Resume the main dialog
      this.dialogPaused = false;
      
      // Use nextTick to avoid reactivity issues
      this.$nextTick(() => {
        // Update the label with the renewed data after dialog is closed
        this.selectedLabel = renewedLabel;
        
        // Force refresh the record labels list
        this.GET_YOUR_RECORD_LABELS({ force: true }).then(() => {
          // Find the renewed label in the refreshed list
          const updatedLabel = this.recordLabels.find(label => label.id === renewedLabel.id);
          if (updatedLabel) {
            this.selectedLabel = updatedLabel;
          }
        });
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.subscription-status {
  font-size: 14px;
  margin-top: 2px;
  
  &.restricted {
    color: #FF4757;
  }
}

/* Record Label Selector Styles - Aligned with AccountSelectionWizard */
.record-label-selector {
  background: #0A0F1D;
  border-radius: 10px;
  overflow: hidden;
  
  .wizard-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    position: relative;
    margin-bottom: 10px;
    
    .heading_wizard {
      margin: 0;
      font-size: 24px;
      font-weight: 500;
      color: #ffffff;
      text-align: center;
      flex-grow: 1;
      padding-bottom: 0;
      line-height: 28px;
    }
    
    .close_modal {
      position: absolute;
      right: 24px;
      top: 50%;
      transform: translateY(-50%);
      width: 20px;
      height: 20px;
      border: none;
      cursor: pointer;
      opacity: 0.7;
      transition: opacity 0.2s ease;
      background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="white"/></svg>') no-repeat center;
      background-size: 20px;
      
      &:hover {
        opacity: 1;
      }
    }
  }
  
  .wrapper_content {
    padding: 0 20px 20px;
    
    .box_create_button {
      position: relative;
      margin-bottom: 20px;
      padding: 15px;
      background: rgba(32, 196, 245, 0.05);
      border: 1px solid rgba(32, 196, 245, 0.2);
      border-radius: 10px;
      cursor: pointer;
      transition: all 0.2s ease;
      overflow: hidden;
      
      &:hover {
        background: rgba(32, 196, 245, 0.1);
      }
      
      &.active {
        background: rgba(32, 196, 245, 0.15);
      }
      
      button {
        position: absolute;
        left: 15px;
        top: 50%;
        transform: translateY(-50%);
        width: 24px;
        height: 24px;
        background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="%2320C4F5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>') no-repeat center;
        border: none;
        padding: 0;
        
        &.active {
          background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="%2320C4F5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>') no-repeat center;
        }
      }
      
      div {
        margin-left: 35px;
        font-size: 16px;
        font-weight: 500;
        color: #20C4F5;
        
        span {
          display: block;
          font-size: 14px;
          font-weight: 400;
          color: rgba(32, 196, 245, 0.7);
          margin-top: 5px;
        }
      }
    }
    
    .list_accounts {
      background: #0C1324;
      border: 1px solid #282C47;
      border-radius: 10px;
      margin-bottom: 20px;
      max-height: 350px;
      overflow-y: auto;
      
      .list-state-container {
        min-height: 200px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 40px 20px;
        text-align: center;
        
        .empty-icon {
          margin-bottom: 20px;
        }
        
        span {
          font-size: 16px;
          color: #7481A6;
        }
        
        .loading-message {
          margin-top: 15px;
          font-size: 16px;
          color: #7481A6;
        }

        .create-btn {
          margin-top: 20px;
          padding: 10px 20px;
          background-color: #20C4F5;
          color: #ffffff;
          border: none;
          border-radius: 4px;
          font-size: 14px;
          cursor: pointer;
          transition: background-color 0.2s ease;
          
          &:hover {
            background-color: darken(#20C4F5, 10%);
          }
        }
      }
      
      .item_account {
        padding: 10px;
        border-bottom: 1px solid rgba(#5F6986, .3);
        cursor: pointer;
        transition: background-color 0.2s;
        background-color: #0C1324;
        display: flex;
        justify-content: space-between;
        align-items: center;
        
        &:last-child {
          border-bottom: none;
        }
        
        &:hover {
          background-color: rgba(#D9D9D9, .1);
        }
        
        &.selected {
          background-color: rgba(32, 196, 245, 0.2);
        }
        
        .data {
          display: flex;
          align-items: center;
          flex-grow: 1;
          
          .img {
            min-width: 47px;
            min-height: 47px;
            width: 47px;
            height: 47px;
            border-radius: 12px;
            overflow: hidden;
            margin-right: 10px;
            background-color: #182034;
            
            img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }
            
            .default_user_avatar {
              width: 100%;
              height: 100%;
              background-color: #5F6986;
              display: flex;
              align-items: center;
              justify-content: center;
              
              img {
                width: 80%;
                height: 80%;
                object-fit: contain;
              }
            }
          }
          
          .description {
            flex-grow: 1;
            
            .name {
              font-weight: 600;
              color: #fff;
              font-size: 14px;
              line-height: 1;
              margin-bottom: 2px;
            }
            
            .type {
              font-size: 12px;
              color: #dcdcdc;
              
              &.restricted {
                color: #E94E4E;
              }
            }
          }
        }
        
        .action {
          display: flex;
          align-items: center;
          padding-right: 25px;
          
          .check_round {
            width: 26px;
            height: 26px;
            border-radius: 50%;
            border: 1px solid #7481A6;
            cursor: pointer;
            z-index: 1;
            
            &.checked {
              border: 1px solid #20C4F5;
              position: relative;
              
              &:after {
                content: '';
                position: absolute;
                width: 12px;
                height: 12px;
                border-radius: 50%;
                background-color: #20C4F5;
                left: 50%;
                top: 50%;
                margin-left: -6px;
                margin-top: -6px;
              }
            }
          }
        }
      }
    }
  }
  
  .wrapper_button {
    display: flex;
    justify-content: center;
    padding: 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    
    &.center {
      justify-content: center;
    }
    
    .tetriatary_btn {
      padding: 10px 20px;
      margin: 0 5px;
      background: transparent;
      border: 1px solid #7481A6;
      border-radius: 4px;
      color: #FFFFFF;
      font-size: 16px;
      cursor: pointer;
      transition: background 0.2s ease;
      
      &:hover {
        background: rgba(255, 255, 255, 0.05);
      }
      
      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}

// Global styles for the v-dialog - these will be applied to the modal
.account_selection_wizard_modal {
  background-color: #0A0F1D;
  border-radius: 10px;
  overflow: hidden;
}
  .dimmed-dialog {
    position: relative;
  }

  .dialog-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.6);
    z-index: 10;
    border-radius: inherit;
  }
</style>
